import React, { useEffect, useState } from "react";
import "./ManageCode.css";
import Button from "../../../components/reusable-button/Button";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { IoBan } from "react-icons/io5";
import { FaHourglass } from "react-icons/fa";
import { MdOutlineDoNotDisturbOff } from "react-icons/md";
import ManageCodeModal, { CodeFormData } from "./code-modal/ManageCodeModal";
import {
  createLicenseKey,
  fetchKeyTypeList,
  fetchLicenseKeyList,
  KeyType,
  LicenseKey,
  updateLicenseKey,
} from "../../../apis/codeApis";
import { toLocalDatetime } from "../../../utils/formatDateUtil";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch } from "react-redux";
import ManageCodeTable, { CodeGroup, CodeRow } from "./ManageCodeTable";
import ReusableLoader from "../../../components/reusable-loader/ReusableLoader";
import {
  getAllCategories,
  getAllCategoryTypes,
} from "../../../apis/categoryApis";

const ActiveIcon = IoIosCheckmarkCircleOutline as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const ExpiredIcon = IoBan as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FullIcon = FaHourglass as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const InactiveIcon = MdOutlineDoNotDisturbOff as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

export interface Option {
  id: string;
  name: string;
}

const ManageCode: React.FC = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editCodeData, setEditCodeData] = useState<any>(null);
  const [licenseKeys, setLicenseKeys] = useState<LicenseKey[]>([]);
  const [codeTypes, setCodeTypes] = useState<KeyType[]>([]);
  const [categories, setCategories] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<
    "active" | "inactive" | "full" | "expired"
  >("active");

  const rowsPerPage = 10;

  const adminDetail = localStorage.getItem("adminDetail");
  const roleName = adminDetail
    ? JSON.parse(adminDetail)?.roleIds?.roleName
    : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [licenseData, codeTypeData] = await Promise.all([
          fetchLicenseKeyList(),
          fetchKeyTypeList(),
        ]);
        if (Array.isArray(licenseData)) {
          setLicenseKeys(licenseData);
        } else {
          console.error("Unexpected response format:", licenseData);
        }
        setCodeTypes(codeTypeData);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCategoriesAndTypes = async () => {
      try {
        const [categoriesRes, categoryTypesRes] = await Promise.all([
          getAllCategories(),
          getAllCategoryTypes(),
        ]);

        const gmetrixTypeIds = categoryTypesRes.data
          .filter((type: any) => type.categoriesTypeName === "GMetrix")
          .map((type: any) => type.id);

        const formatted: Option[] = Array.from(
          new Map(
            categoriesRes.data
              .filter((item: any) =>
                gmetrixTypeIds.includes(item.categoryTypeId)
              )
              .map((item: any) => [
                item.id,
                {
                  id: item.id,
                  name: item.courseName,
                },
              ])
          ).values()
        ) as Option[];

        setCategories(formatted);
      } catch (err) {
        console.error("Error fetching categories or category types:", err);
      }
    };

    fetchCategoriesAndTypes();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleSubmitCode = async (formData: CodeFormData) => {
    setBtnLoading(true);
    const payload = {
      courseId: formData.category?.id!,
      keyTypeId: formData.codeType?.id!,
      maxUsers: formData.usages,
      numCoursesAllowed: formData.maxAllowed,
      thresholdLimit: formData.thresholdAlert,
      // validFrom: toCustomLocalTimestamp(formData.startingDate),
      // validTill: toCustomLocalTimestamp(formData.expiryDate),
      licenseKey: formData.code,
      status: formData.isActive
        ? ("active" as "active")
        : ("inactive" as "inactive"),
      isActive: formData.isActive,
    };

    try {
      if (isEditMode) {
        const result = await updateLicenseKey(editCodeData.id, payload);
        if (result.success) {
          dispatch(
            showSnackbar({
              message: result.data,
              type: "success",
            })
          );
          setIsModalOpen(false);
        }
      } else {
        await createLicenseKey(payload);
        dispatch(
          showSnackbar({
            message: "License key created successfully",
            type: "success",
          })
        );
        setIsModalOpen(false);
      }

      const updatedKeys = await fetchLicenseKeyList();
      setLicenseKeys(updatedKeys);
    } catch (error) {
      console.error("Error submitting license key:", error);
      dispatch(
        showSnackbar({
          message: "Failed to save license key",
          type: "error",
        })
      );
    }
    setBtnLoading(false);
  };

  const groupedData: CodeGroup[] = Object.values(
    (licenseKeys || []).reduce((acc, item) => {
      const category = item?.courseIds?.courseName || "Unknown Category";
      const subCategory =
        item?.keyTypeIds?.keyTypeName || "Unknown Subcategory";
      const key = `${category}-${subCategory}`;

      if (!acc[key]) {
        acc[key] = {
          id: item.id,
          category,
          subCategory,
          rows: [],
        };
      }

      const usagePercent = (item.currentUsage / item.maxUsers) * 100;
      let usageDotColor: "green" | "orange" | "red" | "gray" = "green";
      if (usagePercent >= 100) usageDotColor = "red";
      else if (usagePercent >= item.thresholdLimit) usageDotColor = "orange";

      const now = new Date();
      const isExpired = new Date(item.validTill) < now;
      const isFull = item.currentUsage >= item.maxUsers;
      const inactive = !item.isActive;

      let status: "Active" | "Expired" | "Full" | "Inactive" = "Active";
      let icon = <ActiveIcon />;
      if (isExpired) {
        status = "Expired";
        icon = <ExpiredIcon />;
        usageDotColor = "red";
      } else if (isFull) {
        status = "Full";
        icon = <FullIcon style={{ color: "#707070" }} />;
        usageDotColor = "gray";
      } else if (inactive) {
        status = "Inactive";
        icon = <InactiveIcon style={{ color: "#707070" }} />;
        usageDotColor = "gray";
      } else if (item.currentUsage >= item.thresholdLimit) {
        status = "Active";
        icon = <ActiveIcon />;
        usageDotColor = "orange";
      }

      acc[key].rows.push({
        dateRange: `${new Date(
          item.validFrom
        ).toLocaleDateString()} - ${new Date(
          item.validTill
        ).toLocaleDateString()}`,
        usage: `${item.currentUsage} of ${item.maxUsers}`,
        usageDotColor,
        threshold: item.thresholdLimit,
        status,
        editable: !isExpired && !isFull,
        icon,
        originalData: item,
      });

      return acc;
    }, {} as Record<string, CodeGroup>)
  );

  const handleAddClick = () => {
    setIsEditMode(false);
    setEditCodeData(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (group: CodeGroup, row: CodeRow) => {
    const item = row.originalData;
    setIsEditMode(true);
    setEditCodeData({
      id: item.id,
      category: {
        id: item.courseIds.id,
        name: item.courseIds.courseName,
      },
      codeType: {
        id: item.keyTypeIds.id,
        name: item.keyTypeIds.keyTypeName,
      },
      code: item.licenseKey,
      usages: item.maxUsers,
      thresholdAlert: item.thresholdLimit,
      maxAllowed: item.numCoursesAllowed,
      expiryDate: toLocalDatetime(item.validTill),
      startingDate: toLocalDatetime(item.validFrom),
      isActive: item.isActive,
    });
    setIsModalOpen(true);
  };

  const statusCounts = {
    active: 0,
    inactive: 0,
    full: 0,
    expired: 0,
  };

  groupedData.forEach((group) => {
    group.rows.forEach((row) => {
      const key = row.status.toLowerCase() as keyof typeof statusCounts;
      if (statusCounts[key] !== undefined) {
        statusCounts[key] += 1;
      }
    });
  });

  const filteredGroupedData = groupedData
    .map((group) => {
      const filteredRows = group.rows.filter((row) => {
        // if (activeTab === "all") return true;
        return row.status.toLowerCase() === activeTab;
      });

      return { ...group, rows: filteredRows };
    })
    .filter((group) => group.rows.length > 0);

  const tabs = [
    { key: "active", label: `Active (${statusCounts.active})` },
    { key: "inactive", label: `Inactive (${statusCounts.inactive})` },
    { key: "full", label: `Full (${statusCounts.full})` },
    { key: "expired", label: `Expired (${statusCounts.expired})` },
  ];

  if (!licenseKeys.length && loading) {
    return <ReusableLoader />;
  }

  return (
    <>
      <div className="manage-code-container">
        <div className="manage-code-header">
          <h2>Manage Codes ({licenseKeys?.length})</h2>
          {roleName !== "admin" && (
            <div>
              <Button
                type="button"
                label="+ Add Codes"
                onClick={handleAddClick}
              />
            </div>
          )}
        </div>

        <div className="code-filter-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`code-tab-btn ${
                activeTab === tab.key ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.key as any)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <ManageCodeTable
          groupedData={filteredGroupedData}
          onEditClick={handleEditClick}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      {isModalOpen && (
        <ManageCodeModal
          isEdit={isEditMode}
          initialData={editCodeData}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitCode}
          codeTypes={codeTypes.map((type) => ({
            id: type.id,
            name: type.keyTypeName,
          }))}
          categories={categories}
          loading={btnLoading}
        />
      )}
    </>
  );
};

export default ManageCode;
